import client from "@/client/publicApiClient.ts";
import { useMutation } from "@tanstack/react-query";
import type { SetClientMarketInstrumentStateMutationRequest, SetClientMarketInstrumentStateMutationResponse, SetClientMarketInstrumentStatePathParams, SetClientMarketInstrumentState404 } from "../types/SetClientMarketInstrumentState";
import type { UseMutationOptions } from "@tanstack/react-query";

 type SetClientMarketInstrumentStateClient = typeof client<SetClientMarketInstrumentStateMutationResponse, SetClientMarketInstrumentState404, SetClientMarketInstrumentStateMutationRequest>;
type SetClientMarketInstrumentState = {
    data: SetClientMarketInstrumentStateMutationResponse;
    error: SetClientMarketInstrumentState404;
    request: SetClientMarketInstrumentStateMutationRequest;
    pathParams: SetClientMarketInstrumentStatePathParams;
    queryParams: never;
    headerParams: never;
    response: SetClientMarketInstrumentStateMutationResponse;
    client: {
        parameters: Partial<Parameters<SetClientMarketInstrumentStateClient>[0]>;
        return: Awaited<ReturnType<SetClientMarketInstrumentStateClient>>;
    };
};
/**
 * @description Changes the state of an instrument for a given market and client.
 * @summary Change state
 * @link /api/clients/:clientCode/markets/:marketCode/instruments/:symbol/state
 */
export function useSetClientMarketInstrumentState(clientCode: SetClientMarketInstrumentStatePathParams["clientCode"], marketCode: SetClientMarketInstrumentStatePathParams["marketCode"], symbol: SetClientMarketInstrumentStatePathParams["symbol"], options: {
    mutation?: UseMutationOptions<SetClientMarketInstrumentState["response"], SetClientMarketInstrumentState["error"], SetClientMarketInstrumentState["request"]>;
    client?: SetClientMarketInstrumentState["client"]["parameters"];
} = {}) {
    const { mutation: mutationOptions, client: clientOptions = {} } = options ?? {};
    return useMutation({
        mutationFn: async (data) => {
            const res = await client<SetClientMarketInstrumentState["data"], SetClientMarketInstrumentState["error"], SetClientMarketInstrumentState["request"]>({
                method: "put",
                url: `/api/clients/${clientCode}/markets/${marketCode}/instruments/${symbol}/state`,
                data,
                headers: { "Content-Type": "text/json", ...clientOptions.headers },
                ...clientOptions
            });
            return res.data;
        },
        ...mutationOptions
    });
}